import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { AuthCard } from '../components/AuthCard';
import { Input } from '../components/Input';
import { Button } from '../components/Button';
import { Divider } from '../components/Divider';
import { Alert } from '../components/Alert';

function getRedirectUrl(): string {
  const params = new URLSearchParams(window.location.search);
  const redirectTo = params.get('redirect_to');
  const callbackUrl = new URL('/callback', window.location.origin);
  if (redirectTo) {
    callbackUrl.searchParams.set('redirect_to', redirectTo);
  }
  return callbackUrl.toString();
}

export function SignIn() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect_to');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [githubLoading, setGithubLoading] = useState(false);
  const [gitlabLoading, setGitlabLoading] = useState(false);

  async function handleGitHubSignIn() {
    setError(null);
    setGithubLoading(true);
    try {
      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'github',
        options: { redirectTo: getRedirectUrl() },
      });
      if (oauthError) {
        setError(oauthError.message);
        setGithubLoading(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      setGithubLoading(false);
    }
  }

  async function handleGitLabSignIn() {
    setError(null);
    setGitlabLoading(true);
    try {
      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'gitlab',
        options: { redirectTo: getRedirectUrl() },
      });
      if (oauthError) {
        setError(oauthError.message);
        setGitlabLoading(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      setGitlabLoading(false);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    if (!password) {
      setError('Password is required');
      return;
    }

    setLoading(true);
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (signInError) {
        setError(signInError.message);
        setLoading(false);
        return;
      }

      if (redirectTo) {
        navigate(`/callback?redirect_to=${encodeURIComponent(redirectTo)}`);
      } else {
        navigate('/callback');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      setLoading(false);
    }
  }

  return (
    <AuthCard title="Welcome back" subtitle="Sign in to your LayerLint account">
      {error && <Alert variant="error" message={error} onDismiss={() => setError(null)} />}

      <Button
        variant="github"
        onClick={handleGitHubSignIn}
        loading={githubLoading}
        disabled={loading || gitlabLoading}
      >
        Continue with GitHub
      </Button>

      <Button
        variant="gitlab"
        onClick={handleGitLabSignIn}
        loading={gitlabLoading}
        disabled={loading || githubLoading}
      >
        Continue with GitLab
      </Button>

      <Divider />

      <form onSubmit={handleSubmit}>
        <Input
          label="Email"
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          required
          disabled={loading}
        />
        <Input
          label="Password"
          type="password"
          placeholder="Enter your password"
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
          required
          disabled={loading}
        />

        <div className="flex justify-end mb-4">
          <Link
            to="/forgot-password"
            className="text-sm text-ll-cyan hover:brightness-110 transition-colors"
          >
            Forgot password?
          </Link>
        </div>

        <Button type="submit" loading={loading} disabled={githubLoading || gitlabLoading}>
          Sign in
        </Button>
      </form>

      <p className="text-center text-sm text-ll-muted mt-6">
        Don&apos;t have an account?{' '}
        <Link to="/sign-up" className="text-ll-cyan hover:brightness-110 transition-colors">
          Sign up
        </Link>
      </p>
    </AuthCard>
  );
}
